import type { UseFormReturnType } from '@mantine/form';
import { useContext, useEffect, useRef } from 'react';
import type { ZodObject } from 'zod/v4';
import { FormsRegisterContext } from './context.ts';

export function useRegisterForm(
  id: string,
  // biome-ignore lint/suspicious/noExplicitAny: registry stores heterogeneous form shapes; UseFormReturnType is invariant in its values type, so a bounded generic would reject every concrete form.
  form: UseFormReturnType<any>,
  schema: ZodObject,
) {
  const formsRegisterContext = useContext(FormsRegisterContext);
  if (!formsRegisterContext)
    throw new Error(
      'useRegisterForm must be used within a FormsRegisterProvider',
    );

  // form is a new object each render; captured in a ref so the effect only depends on schema,
  // which is stable across renders and should re-fire the registration when it changes.
  const formRef = useRef(form);
  formRef.current = form;

  const setFormsRegister = formsRegisterContext.setFormsRegister;

  useEffect(() => {
    setFormsRegister((prev) => ({
      ...prev,
      [id]: { form: formRef.current, schema },
    }));
    return () => {
      setFormsRegister((prev) => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
    };
  }, [setFormsRegister, id, schema]);
}
